import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Check, Link2 } from 'lucide-react';
import { api } from '../../lib/api';
import { useTasks } from '../../lib/tasks';
import { WizardDialog } from '../../components/WizardDialog';
import { Button, ErrorNotice, Spinner } from '../../components/ui';

/**
 * What the dry run proposes, verbatim from the API — evidence included,
 * because the operator is the last check before the link is written.
 */
interface Proposal {
  onuId: string;
  onuSerial: string;
  interfaceName: string;
  address: string;
  hostId: number;
  deviceId: string;
  deviceSerial: string;
  ssid: string | null;
  lastInform: number | null;
}

interface Plan {
  proposals: Proposal[];
  ambiguous: Array<{ address: string; onuSerials: string[]; deviceSerials: string[] }>;
  unmatchedDevices: Array<{ deviceId: string; serial: string; mgmtIp: string | null }>;
  onusRead: number;
  onusUnreadable: number;
}

/**
 * Pair ONUs with the ACS devices that ARE them, when their serials disagree.
 *
 * Two steps by design: the dry run reads the chassis and lays the proposed
 * pairs out with their evidence — the address both sides agree on, and the
 * SSID the subscriber would recognise — and nothing is written until the
 * operator applies exactly the pairs they saw. A wrong link shows one
 * subscriber another's WiFi and aims writes at another's CPE, which is why
 * ambiguous addresses are parked rather than guessed.
 *
 * The scan is a read and stays in the dialog. The apply is the write, so it
 * CLOSES the dialog and runs in the tasks tray; the toast counts the pairs.
 */
export function LinkDevicesDialog({
  oltId,
  oltName,
  onClose,
}: {
  oltId: string;
  oltName: string;
  onClose: () => void;
}) {
  const tasks = useTasks();
  const [plan, setPlan] = useState<Plan | null>(null);
  const [error, setError] = useState<unknown>(null);

  const scan = useMutation({
    mutationFn: () => api.post<Plan>(`/olts/${encodeURIComponent(oltId)}/link-devices`, { dryRun: true }),
    onSuccess: (result) => {
      setPlan(result);
      setError(null);
    },
    onError: setError,
  });

  const startApply = () => {
    if (!plan) return;
    const pairs = plan.proposals.map((p) => ({ onuId: p.onuId, deviceId: p.deviceId }));
    onClose();
    tasks.start<{ results: Array<{ linked: boolean; reason: string | null }> }>({
      label: `Link ACS devices — ${oltName}`,
      href: `/olts/${encodeURIComponent(oltId)}`,
      run: () =>
        api.post<{ results: Array<{ linked: boolean; reason: string | null }> }>(
          `/olts/${encodeURIComponent(oltId)}/link-devices`,
          { dryRun: false, pairs },
        ),
      done: ({ results }) => {
        const linked = results.filter((r) => r.linked).length;
        const skipped = results.length - linked;
        return (
          `${linked} subscriber${linked === 1 ? '' : 's'} on ${oltName} linked to their ACS device` +
          (skipped > 0 ? `; ${skipped} skipped — the state had moved since the scan.` : '.')
        );
      },
      invalidate: [['olt'], ['onus'], ['onu']],
    });
  };

  return (
    <WizardDialog
      title={
        <span className="flex items-center gap-2">
          <Link2 className="size-4 text-muted" />
          Link ACS devices on {oltName}
        </span>
      }
      subtitle="Some routers announce a different serial to TR-069 than their chassis knows, so the inventory sweep cannot pair them. This reads each unlinked ONU’s management address off the chassis and matches it against the address each unclaimed device informs from — exactly, or not at all."
      maxWidth="2xl"
      bodyClassName="grid gap-4 p-5"
      // Scan → Review pills, the OMCI/TR-069 management-mode look; Review is
      // reached once the dry run has laid the proposed pairs out.
      steps={[
        { key: 'scan', title: 'Scan' },
        { key: 'review', title: 'Review' },
      ]}
      currentStep={plan ? 1 : 0}
      onClose={onClose}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          {!plan && (
            <Button
              variant="primary"
              icon={<Link2 className="size-4" />}
              loading={scan.isPending}
              onClick={() => scan.mutate()}
            >
              Scan for matches
            </Button>
          )}
          {plan && (
            <>
              <Button variant="secondary" loading={scan.isPending} onClick={() => scan.mutate()}>
                Re-scan
              </Button>
              <Button
                variant="primary"
                icon={<Check className="size-4" />}
                disabled={plan.proposals.length === 0}
                onClick={startApply}
              >
                Apply
              </Button>
            </>
          )}
        </div>
      }
    >
      {error != null && <ErrorNotice error={error} />}

      {!plan && (
        <p className="text-sm text-muted">
          The scan reads the chassis — one command per unlinked ONU — and writes nothing.
        </p>
      )}

      {plan && (
        <>
              <div className="text-xs text-faint">
                {plan.onusRead} ONU{plan.onusRead === 1 ? '' : 's'} read
                {plan.onusUnreadable > 0
                  ? `; ${plan.onusUnreadable} did not answer (offline ONUs are normal here)`
                  : ''}
                .
              </div>

              {plan.proposals.length === 0 ? (
                <p className="text-sm text-muted">
                  No pair agreed on an address. Devices that did not match are listed below — a
                  device that last informed before its address changed cannot be matched until it
                  informs again.
                </p>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-line">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-line bg-surface-2 text-left text-xs uppercase tracking-wide text-faint">
                        <th className="px-3 py-2 font-medium">ONU on the chassis</th>
                        <th className="px-3 py-2 font-medium">Agrees on</th>
                        <th className="px-3 py-2 font-medium">ACS device</th>
                      </tr>
                    </thead>
                    <tbody>
                      {plan.proposals.map((p) => (
                        <tr key={p.onuId} className="border-b border-line last:border-b-0">
                          <td className="px-3 py-2">
                            <div className="font-mono text-xs">{p.onuSerial}</div>
                            <div className="text-xs text-faint">{p.interfaceName}</div>
                          </td>
                          <td className="px-3 py-2 font-mono text-xs">{p.address}</td>
                          <td className="px-3 py-2">
                            <div className="font-mono text-xs">{p.deviceSerial}</div>
                            {/* The SSID is the detail an operator can check with
                                the subscriber — serials mean nothing to anybody
                                on the phone; the WiFi name does. */}
                            {p.ssid && <div className="text-xs text-faint">SSID “{p.ssid}”</div>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {plan.ambiguous.length > 0 && (
                <div className="rounded-lg border border-warn/30 bg-warn-soft px-3 py-2 text-sm text-warn">
                  {plan.ambiguous.length} address{plan.ambiguous.length === 1 ? '' : 'es'} claimed
                  by more than one row —{' '}
                  {plan.ambiguous
                    .map((a) => `${a.address} (${[...a.onuSerials, ...a.deviceSerials].join(', ')})`)
                    .join('; ')}
                  . Parked rather than guessed: a wrong link aims writes at another subscriber&rsquo;s
                  CPE.
                </div>
              )}

              {plan.unmatchedDevices.length > 0 && (
                <div className="text-xs text-faint">
                  Still unmatched: {plan.unmatchedDevices.map((d) => d.serial).join(', ')}.
                </div>
              )}
            </>
          )}

          {plan === null && scan.isPending && (
            <div className="flex justify-center p-4">
              <Spinner />
            </div>
          )}
    </WizardDialog>
  );
}
